import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { businessToday, businessTodayISO } from '../../businessDay';
import {
    Box, Typography, Stack, Chip, CircularProgress,
    Input, Select, Option, IconButton, Button,
    Modal, ModalDialog, ModalClose, Divider, Textarea, Checkbox,
    Avatar,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import ArrowBackRoundedIcon             from '@mui/icons-material/ArrowBackRounded';
import GavelRoundedIcon                 from '@mui/icons-material/GavelRounded';
import AddRoundedIcon                   from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon                from '@mui/icons-material/DeleteRounded';
import BlockRoundedIcon                 from '@mui/icons-material/BlockRounded';
import CalendarTodayRoundedIcon         from '@mui/icons-material/CalendarTodayRounded';
import RemoveCircleOutlineRoundedIcon   from '@mui/icons-material/RemoveCircleOutlineRounded';
import MoneyOffRoundedIcon              from '@mui/icons-material/MoneyOffRounded';
import AdminPanelSettingsRoundedIcon    from '@mui/icons-material/AdminPanelSettingsRounded';
import UndoRoundedIcon                  from '@mui/icons-material/UndoRounded';

import UndoToast from '../../components/UndoToast';
import DateQuickPicker from '../../components/DateQuickPicker';
import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIGetFines, APICreateFine, APICancelFine, APIDeleteFine, APIBulkDeleteFines,
    APIGetEmployees, APIGetLocations,
    parseApiError,
} from '../../api';
import MemeLoader from '../../components/MemeLoader';
import useMultiSelect from '../../hooks/useMultiSelect';
import SelectionToolbar from '../../components/SelectionToolbar';
import EmployeeMultiSelect from '../../components/EmployeeMultiSelect';



// ─── Утилиты ──────────────────────────────────────────────────────────────────
const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const getMonthStart = () => {
    const now = businessToday();
    return toISO(new Date(now.getFullYear(), now.getMonth(), 1));
};

const fmtDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', weekday: 'short' });
};

const fmtMoney = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.04 } } };
const fadeUp  = { hidden: { opacity: 0, y: 14 }, visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } } };


// ════════════════════════════════════════════════════════════════════
//  FinesPage (менеджер)
// ════════════════════════════════════════════════════════════════════
export default function FinesPage() {
    const { token, showToast, onRefreshTick, hasPerm } = useContext(AppContext);
    const navigate = useNavigate();

    const canCreate = hasPerm('finance.fines.create');
    const canDelete = hasPerm('finance.fines.delete');
    const canCancel = hasPerm('finance.fines.cancel');

    // ── Справочники ───────────────────────────────────────────────
    const [employees, setEmployees] = useState([]);
    const [locations, setLocations] = useState([]);

    // ── Состояние списка ──────────────────────────────────────────
    const [fines,        setFines]        = useState([]);
    const [tab,          setTab]          = useState('all'); // all | manual | auto
    const [loading,      setLoading]      = useState(true);
    const [dateFrom,     setDateFrom]     = useState(getMonthStart);
    const [dateTo,       setDateTo]       = useState(businessTodayISO);
    const [filterPhId,   setFilterPhId]   = useState('');
    const [filterType,   setFilterType]   = useState('');   // '' | 'fine' | 'deduction'
    const [filterRole, setFilterRole] = useState('');   // '' | 'admin' | 'photo'
    const [activePeriod, setActivePeriod] = useState('Месяц');
    const [undoFine, setUndoFine] = useState(null);

    // ── Модалка добавления ────────────────────────────────────────
    const [addOpen,        setAddOpen]        = useState(false);
    const [addPhIds,       setAddPhIds]       = useState([]);
    const [addLocId,       setAddLocId]       = useState('');
    const [addValue,       setAddValue]       = useState('');
    const [addDate,        setAddDate]        = useState(businessTodayISO);
    const [addReason,      setAddReason]      = useState('');
    const [addIsDeduction, setAddIsDeduction] = useState(false);
    const [addIsAdmin,     setAddIsAdmin]     = useState(false);
    const [submitting,     setSubmitting]     = useState(false);
    const [lastCreated,    setLastCreated] = useState(null);

    // ── Модалки действий ──────────────────────────────────────────
    const [cancelId,  setCancelId]  = useState(null);
    const [canceling, setCanceling] = useState(false);
    const [deleteId,  setDeleteId]  = useState(null);
    const [deleting,  setDeleting]  = useState(false);

    const sel = useMultiSelect();
    const [bulkDeleting, setBulkDeleting] = useState(false);

    const handleBulkDelete = async () => {
        if (sel.count === 0) return;
        if (!window.confirm(`Удалить выбранные штрафы (${sel.count})? Действие необратимо.`)) return;
        setBulkDeleting(true);
        const res = await APIBulkDeleteFines(token, sel.ids);
        if (res?.error) {
            showToast(parseApiError(res.error), 'danger');
        } else {
            showToast(`Удалено штрафов: ${res.deleted ?? sel.ids.length}`);
            sel.exitSelectMode();
            loadFines();
        }
        setBulkDeleting(false);
    };

    // ── Загрузка справочников ─────────────────────────────────────
    useEffect(() => {
        (async () => {
            const [empl, locs] = await Promise.all([
                APIGetEmployees(token, 'can_have_orders'),
                APIGetLocations(token),
            ]);
            if (Array.isArray(empl)) setEmployees(empl);
            if (Array.isArray(locs)) {
                setLocations(locs);
                if (locs.length === 1) setAddLocId(locs[0].id);
            }
        })();
    }, [token]);

    // ── Загрузка штрафов ──────────────────────────────────────────
    const loadFines = useCallback(async () => {
        setLoading(true);
        const params = {};
        if (dateFrom)   params.date_from       = dateFrom;
        if (dateTo)     params.date_to         = dateTo;
        if (filterPhId) params.photographer_id = filterPhId;
        if (filterType === 'fine')      params.is_deduction = 'false';
        if (filterType === 'deduction') params.is_deduction = 'true';
        if (filterRole === 'admin') params.recipient_role = 'admin';
        if (filterRole === 'photo') params.recipient_role = 'photo';

        const data = await APIGetFines(token, params);
        if (Array.isArray(data)) setFines(data);
        setLoading(false);
    }, [token, dateFrom, dateTo, filterPhId, filterType, filterRole]);

    useEffect(() => { loadFines(); }, [loadFines]);
    useEffect(() => onRefreshTick(loadFines), [onRefreshTick, loadFines]);

    // ── Быстрый выбор периода ─────────────────────────────────────
    const selectPeriod = (p) => { setActivePeriod(p.label); setDateFrom(p.from); setDateTo(p.to); };

    // ── Итого ─────────────────────────────────────────────────────
    const totals = useMemo(() => {
        const active   = fines.filter(f => f.is_active);
        const fineList = active.filter(f => !f.is_deduction);
        const dedList  = active.filter(f => f.is_deduction);
        const fineSum  = fineList.reduce((s, f) => s + parseFloat(f.value || 0), 0);
        const dedSum   = dedList.reduce((s, f) => s + parseFloat(f.value || 0), 0);
        const canceled = fines.filter(f => !f.is_active).length;

        // Разбивка по роли
        const adminFines = active.filter(f => f.is_administrator);
        const photoFines = active.filter(f => !f.is_administrator);
        const adminSum   = adminFines.reduce((s, f) => s + parseFloat(f.value || 0), 0);
        const photoSum   = photoFines.reduce((s, f) => s + parseFloat(f.value || 0), 0);

        return {
            total: fineSum + dedSum, fineSum, dedSum,
            count: active.length, fineCount: fineList.length, dedCount: dedList.length,
            canceled,
            adminSum, adminCount: adminFines.length,
            photoSum, photoCount: photoFines.length,
        };
    }, [fines]);

    // ── Счётчики по типам (по всему массиву fines) ──────────────────
    const { allCount, manualCount, autoCount } = useMemo(() => {
        let manual = 0, auto = 0;
        for (const f of fines) {
            if (f.is_auto) auto++;
            else manual++;
        }
        return {
            allCount:    fines.length,
            manualCount: manual,
            autoCount:   auto,
        };
    }, [fines]);

    // ── Отфильтрованные по выбранному табу ──────────────────────────
    const filteredFines = useMemo(() => {
        if (tab === 'manual') return fines.filter(f => !f.is_auto);
        if (tab === 'auto')   return fines.filter(f =>  f.is_auto);
        return fines;
    }, [fines, tab]);

    // ── Группировка по дате (для отображения) ────────────────────────
    const grouped = useMemo(() => {
        const map = {};
        const sorted = [...filteredFines].sort((a, b) => (b.date || '').localeCompare(a.date || '') || b.id - a.id);
        for (const f of sorted) {
            const key = f.date || 'unknown';
            if (!map[key]) map[key] = [];
            map[key].push(f);
        }
        return Object.entries(map);
    }, [filteredFines]);

    // Выбираем из видимого (отфильтрованного) списка штрафов.
    const selectableIds = useMemo(
        () => filteredFines.map(f => f.id),
        [filteredFines],
    );

    // ── Добавление ────────────────────────────────────────────────
    const handleAdd = async () => {
        if (!addPhIds.length) { showToast('Выберите хотя бы одного сотрудника', 'warning'); return; }
        if (!addLocId)  { showToast('Выберите точку', 'warning'); return; }
        if (!addValue || parseFloat(addValue) <= 0) { showToast('Укажите сумму', 'warning'); return; }
        if (!addReason.trim()) { showToast('Укажите причину', 'warning'); return; }
        if (!addDate)   { showToast('Укажите дату', 'warning'); return; }

        setSubmitting(true);
        const res = await APICreateFine(token, {
            photographer_ids: addPhIds,
            location_id:      addLocId,
            value:            parseFloat(addValue),
            reason:           addReason.trim(),
            is_deduction:     addIsDeduction,
            is_administrator: addIsAdmin,
            date:             addDate,
        });

        if (res?.error) {
            showToast(parseApiError(res.error), 'danger');
        } else {
            // Сервер возвращает массив, если фотографов > 1
            const fines = Array.isArray(res) ? res : [res];
            setLastCreated(fines[0]);
            const totalSum = fines.reduce((s, f) => s + parseFloat(f.value), 0);
            const word = fines[0].is_deduction ? 'списания' : 'штрафы';
            const message = fines.length === 1
                ? `${fines[0].is_deduction ? 'Списание' : 'Штраф'} добавлен — ${fmtMoney(fines[0].value)} ₽`
                : `Добавлены ${word}: ${fines.length} шт. на ${fmtMoney(totalSum)} ₽`;
            setUndoFine({ id: fines[0].id, ids: fines.map(f => f.id), message });
            setAddOpen(false);
            resetAddForm();
            loadFines();
        }
        setSubmitting(false);
    };

    const resetAddForm = () => {
        setAddPhIds([]);
        setAddLocId(locations.length === 1 ? locations[0].id : '');
        setAddValue('');
        setAddDate(businessTodayISO());
        setAddReason('');
        setAddIsDeduction(false);
        setAddIsAdmin(false);
    };

    // ── Отмена (soft-delete) ──────────────────────────────────────
    const handleCancel = async () => {
        if (!cancelId) return;
        setCanceling(true);
        const res = await APICancelFine(token, cancelId);
        if (res?.error) {
            showToast(parseApiError(res.error), 'danger');
        } else {
            showToast('Штраф отменён');
            setCancelId(null);
            loadFines();
        }
        setCanceling(false);
    };

    const handleUndoFine = async () => {
        if (!undoFine) return;
        const res = await APIDeleteFine(token, undoFine.id);
        if (res?.error) {
            showToast(parseApiError(res.error), 'danger');
        } else {
            showToast('Штраф отменён');
            loadFines();
        }
        setUndoFine(null);
    };

    // ── Удаление ──────────────────────────────────────────────────
    const handleDelete = async () => {
        if (!deleteId) return;
        setDeleting(true);
        const res = await APIDeleteFine(token, deleteId);
        if (res?.error) {
            showToast(parseApiError(res.error), 'danger');
        } else {
            showToast('Удалено');
            setDeleteId(null);
            loadFines();
        }
        setDeleting(false);
    };

    // ── Рендер ────────────────────────────────────────────────────
    return (
        <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 600, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* ── Шапка ─────────────────────────────────────── */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <IconButton variant="plain" onClick={() => navigate('/manager')}
                            sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                            <ArrowBackRoundedIcon />
                        </IconButton>
                        <Box>
                            <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                Штрафы и списания
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                                Управление взысканиями
                            </Typography>
                        </Box>
                    </Box>
                    {canCreate && (
                        <Button
                            size="sm"
                            startDecorator={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                            onClick={() => setAddOpen(true)}
                            sx={{
                                background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                color: '#fff', fontWeight: 700, fontSize: 13, border: 'none',
                                boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                                '&:hover': { boxShadow: '0 6px 24px rgba(162,38,226,0.5)' },
                            }}
                        >
                            Добавить
                        </Button>
                    )}
                </Box>

                {/* ── Фильтры ──────────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    {/* Быстрые периоды */}
                    <Box sx={{ mb: 2 }}>
                        <DateQuickPicker
                            activeKey={activePeriod}
                            onChange={(from, to, key) => {
                                setDateFrom(from);
                                setDateTo(to);
                                setActivePeriod(key);
                            }}
                        />
                    </Box>

                    {/* Даты */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                        <Input type="date" size="sm" value={dateFrom}
                            onChange={(e) => { setDateFrom(e.target.value); setActivePeriod(''); }}
                        />
                        <Input type="date" size="sm" value={dateTo}
                            onChange={(e) => { setDateTo(e.target.value); setActivePeriod(''); }}
                        />
                    </Box>

                    {/* Сотрудник + тип */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
                        <Select value={filterPhId} onChange={(_, v) => setFilterPhId(v || '')}
                            placeholder="Все сотрудники" size="sm"
                        >
                            <Option value="">Все сотрудники</Option>
                            {employees.map(e => (
                                <Option key={e.id} value={e.id}>{e.first_name} {e.last_name}</Option>
                            ))}
                        </Select>
                        <Select value={filterType} onChange={(_, v) => setFilterType(v || '')}
                            placeholder="Все типы" size="sm"
                        >
                            <Option value="">Все типы</Option>
                            <Option value="fine">Штрафы</Option>
                            <Option value="deduction">Списания</Option>
                        </Select>
                    </Box>

                    {/* Роль (админ / фотограф) */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
                        <Select value={filterRole} onChange={(_, v) => setFilterRole(v || '')}
                            placeholder="Все роли" size="sm"
                        >
                            <Option value="">Все роли</Option>
                            <Option value="photo">Фотографам</Option>
                            <Option value="admin">Администраторам</Option>
                        </Select>
                    </Box>
                </motion.div>

                {/* ── Итого-карточки ────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.08 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 1, mb: 2.5 }}>
                        {/* Общая */}
                        <Box sx={{
                            gridColumn: '1 / -1', px: 2, py: 1.8,
                            borderRadius: 'var(--joy-radius-lg)',
                            border: '1px solid var(--joy-palette-divider)',
                            bgcolor: 'rgba(239,68,68,0.06)',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                            <Box>
                                <Box sx={{ color: '#ef4444', mb: 0.5 }}>
                                    <GavelRoundedIcon sx={{ fontSize: 20 }} />
                                </Box>
                                <Typography className="amount-negative" sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1 }}>
                                    {fmtMoney(totals.total)} ₽
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.4 }}>
                                    Активных · {totals.count}
                                    {totals.canceled > 0 && ` · отменённых ${totals.canceled}`}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Штрафы */}
                        <Box sx={{ px: 2, py: 1.4, borderRadius: 'var(--joy-radius-lg)', border: '1px solid var(--joy-palette-divider)', bgcolor: 'rgba(239,68,68,0.06)' }}>
                            <Box sx={{ color: '#ef4444', mb: 0.3 }}><BlockRoundedIcon sx={{ fontSize: 16 }} /></Box>
                            <Typography className="amount-negative" sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1 }}>
                                {fmtMoney(totals.fineSum)}
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                                Штрафы · {totals.fineCount}
                            </Typography>
                        </Box>

                        {/* Списания */}
                        <Box sx={{ px: 2, py: 1.4, borderRadius: 'var(--joy-radius-lg)', border: '1px solid var(--joy-palette-divider)', bgcolor: 'rgba(245,158,11,0.06)' }}>
                            <Box sx={{ color: '#f59e0b', mb: 0.3 }}><MoneyOffRoundedIcon sx={{ fontSize: 16 }} /></Box>
                            <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#f59e0b', lineHeight: 1 }}>
                                {fmtMoney(totals.dedSum)}
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                                Списания · {totals.dedCount}
                            </Typography>
                        </Box>
                    </Box>
                </motion.div>

                {/* ── Переключатель: Все / Ручные / Авто ──────────────────── */}
                <Box sx={{
                    display: 'inline-flex',
                    p: 0.4,
                    mb: 2,
                    borderRadius: 'var(--joy-radius-lg)',
                    border: '1px solid var(--joy-palette-divider)',
                    bgcolor: 'var(--joy-palette-background-level1)',
                    gap: 0.3,
                    flexWrap: 'wrap',
                }}>
                    {[
                        {
                            value: 'all',
                            label: 'Все',
                            count: allCount,
                            color: 'var(--joy-palette-primary-400)',
                            bg: 'rgba(162,38,226,0.10)',
                        },
                        {
                            value: 'manual',
                            label: 'Ручные',
                            count: manualCount,
                            color: '#22c55e',
                            bg: 'rgba(34,197,94,0.10)',
                        },
                        {
                            value: 'auto',
                            label: '⚙ Авто',
                            count: autoCount,
                            color: '#f59e0b',
                            bg: 'rgba(245,158,11,0.10)',
                        },
                    ].map(opt => {
                        const active = tab === opt.value;
                        return (
                            <Box
                                key={opt.value}
                                onClick={() => setTab(opt.value)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.8,
                                    px: 1.8, py: 0.9,
                                    borderRadius: 'var(--joy-radius-md)',
                                    bgcolor: active ? opt.bg : 'transparent',
                                    border: '1px solid',
                                    borderColor: active ? `${opt.color}40` : 'transparent',
                                    cursor: 'pointer',
                                    transition: 'all .15s',
                                    '&:hover': {
                                        bgcolor: active ? opt.bg : 'var(--joy-palette-background-level2)',
                                    },
                                }}
                            >
                                <Typography sx={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: active ? opt.color : 'var(--joy-palette-text-secondary)',
                                    transition: 'color .15s',
                                }}>
                                    {opt.label}
                                </Typography>
                                <Chip
                                    size="sm"
                                    sx={{
                                        bgcolor: active ? `${opt.color}25` : 'var(--joy-palette-background-level2)',
                                        color: active ? opt.color : 'var(--joy-palette-text-tertiary)',
                                        fontWeight: 800,
                                        fontSize: 10,
                                        minHeight: 18,
                                        px: 0.8,
                                        border: 'none',
                                    }}
                                >
                                    {opt.count}
                                </Chip>
                            </Box>
                        );
                    })}
                </Box>

                {/* ── Список ───────────────────────────────────── */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : filteredFines.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <GavelRoundedIcon sx={{ fontSize: 48, color: 'var(--joy-palette-text-tertiary)', mb: 1.5 }} />
                        <Typography sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }}>
                            {tab === 'auto'   ? 'Нет автоматических штрафов, так держать 👏' :
                            tab === 'manual' ? 'Нет ручных штрафов, так держать 👏'         :
                            'За выбранный период нет штрафов, так держать 👏'}
                        </Typography>
                    </Box>
                ) : (
                    <motion.div variants={stagger} initial="hidden" animate="visible">
                        <Stack spacing={1.5}>

                            {canDelete && selectableIds.length > 0 && (
                                <SelectionToolbar
                                    selectMode={sel.selectMode}
                                    onEnter={() => sel.setSelectMode(true)}
                                    onExit={sel.exitSelectMode}
                                    count={sel.count}
                                    totalSelectable={selectableIds.length}
                                    allChecked={selectableIds.length > 0 && sel.count === selectableIds.length}
                                    onToggleAll={(e) => e.target.checked ? sel.selectAll(selectableIds) : sel.clear()}
                                    onDelete={handleBulkDelete}
                                    deleting={bulkDeleting}
                                    enterLabel="Выбрать несколько"
                                    deleteLabel="Удалить штрафы"
                                />
                            )}
                            
                            {grouped.map(([date, items]) => {
                                const dayTotal = items.filter(f => f.is_active).reduce((s, f) => s + parseFloat(f.value || 0), 0);
                                return (
                                    <motion.div key={date} variants={fadeUp}>
                                        <Box sx={{
                                            bgcolor: 'var(--joy-palette-background-level1)',
                                            border: '1px solid var(--joy-palette-divider)',
                                            borderRadius: 'var(--joy-radius-xl)',
                                            overflow: 'hidden',
                                        }}>
                                            {/* Заголовок дня */}
                                            <Box sx={{
                                                px: 2, py: 1,
                                                borderBottom: '1px solid var(--joy-palette-divider)',
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                                    <CalendarTodayRoundedIcon sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }} />
                                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                        {fmtDate(date)}
                                                    </Typography>
                                                </Box>
                                                <Typography className="amount-negative" sx={{ fontSize: 13, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                                                    {fmtMoney(dayTotal)} ₽
                                                </Typography>
                                            </Box>

                                            {/* Строки */}
                                            {items.map((fine, idx) => {
                                                const isCanceled = !fine.is_active;
                                                return (
                                                    <Box key={fine.id} sx={{
                                                        display: 'flex', alignItems: 'center', gap: 1,
                                                        px: 2, py: 1.2,
                                                        borderBottom: idx < items.length - 1 ? '1px solid var(--joy-palette-divider)' : 'none',
                                                        opacity: isCanceled ? 0.45 : 1,
                                                    }}>
                                                        {sel.selectMode && canDelete && (
                                                            <Checkbox size="sm"
                                                                checked={sel.isSelected(fine.id)}
                                                                onChange={() => sel.toggle(fine.id)}
                                                                sx={{ mr: 0.5, flexShrink: 0 }} />
                                                        )}

                                                        {/* Аватар */}
                                                        <Avatar src={fine.photographer_avatar_url} size="sm" sx={{
                                                            width: 32, height: 32, fontSize: 11, fontWeight: 700,
                                                            bgcolor: 'rgba(239,68,68,0.12)', color: '#f87171',
                                                            border: '1.5px solid rgba(239,68,68,0.4)', flexShrink: 0,
                                                        }}>
                                                            {(fine.photographer_name || '').charAt(0).toUpperCase()}
                                                        </Avatar>

                                                        {/* Инфо */}
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography sx={{
                                                                fontSize: 13, fontWeight: 700,
                                                                color: 'var(--joy-palette-text-primary)',
                                                                textDecoration: isCanceled ? 'line-through' : 'none',
                                                            }}>
                                                                {fine.photographer_name}
                                                            </Typography>
                                                            <Typography sx={{
                                                                fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.2,
                                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                            }}>
                                                                {fine.location_name} · <Box component="span" sx={{ fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>{fine.reason}</Box>
                                                            </Typography>
                                                            {fine.created_by_name && (
                                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                                                    Выставил: <Box component="span" sx={{ color: '#06b6d4', fontWeight: 700 }}>{fine.created_by_name}</Box> · {new Date(fine.date_create).toLocaleDateString('ru-RU')}
                                                                </Typography>
                                                            )}
                                                        </Box>

                                                        {/* Чипы */}
                                                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                                            {fine.is_auto && (
                                                                <Chip size="sm" variant="soft" sx={{
                                                                    fontSize: 10, fontWeight: 700,
                                                                    bgcolor: 'rgba(245,158,11,0.10)',
                                                                    color: '#f59e0b',
                                                                }}>
                                                                    ⚙ Авто
                                                                </Chip>
                                                            )}
                                                            {fine.is_administrator && (
                                                                <Chip size="sm" variant="soft" sx={{ fontSize: 10, fontWeight: 700, bgcolor: 'rgba(245,158,11,0.10)', color: '#f59e0b' }}
                                                                    startDecorator={<AdminPanelSettingsRoundedIcon sx={{ fontSize: 12 }} />}
                                                                >
                                                                    Админ
                                                                </Chip>
                                                            )}
                                                            <Chip size="sm" variant="soft" sx={{
                                                                fontSize: 10, fontWeight: 700,
                                                                bgcolor: fine.is_deduction ? 'rgba(245,158,11,0.10)' : 'rgba(239,68,68,0.10)',
                                                                color: fine.is_deduction ? '#f59e0b' : '#ef4444',
                                                            }}>
                                                                {fine.is_deduction ? 'Списание' : 'Штраф'}
                                                            </Chip>
                                                            {isCanceled && (
                                                                <Chip size="sm" variant="soft" sx={{ fontSize: 10, fontWeight: 700, bgcolor: 'rgba(100,100,130,0.10)', color: 'var(--joy-palette-text-tertiary)' }}>
                                                                    Отменён
                                                                </Chip>
                                                            )}
                                                        </Box>

                                                        {/* Сумма */}
                                                        <Typography sx={{
                                                            fontSize: 15, fontWeight: 800,
                                                            color: isCanceled
                                                                ? 'var(--joy-palette-text-tertiary)'
                                                                : fine.is_deduction ? '#f59e0b' : '#ef4444',
                                                            minWidth: 50, textAlign: 'right',
                                                            fontVariantNumeric: 'tabular-nums',
                                                            textDecoration: isCanceled ? 'line-through' : 'none',
                                                        }}>
                                                            -{fmtMoney(fine.value)}
                                                        </Typography>

                                                        {/* Действия */}
                                                        {!isCanceled && canCancel && (
                                                            <IconButton size="sm" variant="solid" color="warning"
                                                                onClick={() => setCancelId(fine.id)}
                                                                title="Отменить штраф"
                                                                sx={{ flexShrink: 0 }}
                                                            >
                                                                <UndoRoundedIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        )}
                                                        {canDelete && (
                                                            <IconButton size="sm" variant="solid" color="danger"
                                                                onClick={() => setDeleteId(fine.id)}
                                                                title="Удалить штраф"
                                                                sx={{ flexShrink: 0 }}
                                                            >
                                                                <DeleteRoundedIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </motion.div>
                                );
                            })}
                        </Stack>
                    </motion.div>
                )}
            </Box>


            {/* ═══════ Модалка добавления ═══════ */}
            <Modal open={addOpen} onClose={() => setAddOpen(false)}>
                <ModalDialog sx={{
                    maxWidth: 440, width: '100%',
                    bgcolor: 'var(--joy-palette-background-surface)',
                    border: '1px solid var(--joy-palette-divider)',
                    borderRadius: 'var(--joy-radius-xl)',
                    p: { xs: 2, sm: 2.5 },
                }}>
                    <ModalClose />
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mb: 2 }}>
                        Новый штраф / списание
                    </Typography>

                    <Stack spacing={2}>
                        {/* Тип: штраф или списание */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {[
                                { value: false, label: 'Штраф',    color: '#ef4444', bg: 'rgba(239,68,68,0.10)', icon: <BlockRoundedIcon sx={{ fontSize: 14 }} /> },
                                { value: true,  label: 'Списание', color: '#f59e0b', bg: 'rgba(245,158,11,0.10)', icon: <MoneyOffRoundedIcon sx={{ fontSize: 14 }} /> },
                            ].map(t => {
                                const active = addIsDeduction === t.value;
                                return (
                                    <Chip key={String(t.value)} size="md" variant={active ? 'solid' : 'outlined'}
                                        onClick={() => setAddIsDeduction(t.value)}
                                        startDecorator={t.icon}
                                        sx={{
                                            flex: 1, justifyContent: 'center',
                                            cursor: 'pointer', fontWeight: 700, fontSize: 13,
                                            bgcolor: active ? t.bg : 'transparent',
                                            color: active ? t.color : 'var(--joy-palette-text-tertiary)',
                                            borderColor: active ? t.color : 'var(--joy-palette-divider)',
                                            transition: 'all .15s',
                                            '&:hover': { borderColor: t.color },
                                        }}
                                    >
                                        {t.label}
                                    </Chip>
                                );
                            })}
                        </Box>

                        {/* Сотрудник */}
                        <EmployeeMultiSelect
                            label="Сотрудники"
                            placeholder="Выберите фотографа(ов)"
                            employees={employees}
                            value={addPhIds}
                            onChange={setAddPhIds}
                        />

                        {/* Точка + Дата */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Точка
                                </Typography>
                                <Select value={addLocId} onChange={(_, v) => setAddLocId(v || '')}
                                    placeholder="Точка" size="sm"
                                >
                                    {locations.map(l => (
                                        <Option key={l.id} value={l.id}>{l.name}</Option>
                                    ))}
                                </Select>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Дата
                                </Typography>
                                <Input type="date" size="sm" value={addDate}
                                    onChange={(e) => setAddDate(e.target.value)}
                                />
                            </Box>
                        </Box>

                        {/* Сумма */}
                        <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                Сумма
                            </Typography>
                            <Input type="number" size="sm" placeholder="0" value={addValue}
                                onChange={(e) => setAddValue(e.target.value)}
                                endDecorator={<Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>₽</Typography>}
                            />
                        </Box>

                        {/* Причина */}
                        <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                Причина
                            </Typography>
                            <Textarea size="sm" minRows={2} maxRows={4}
                                placeholder="За что штраф / списание..."
                                value={addReason} onChange={(e) => setAddReason(e.target.value)}
                            />
                        </Box>

                        {/* Чекбокс — как администратору */}
                        <Box onClick={() => setAddIsAdmin(p => !p)} sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1,
                            borderRadius: 'var(--joy-radius-lg)',
                            bgcolor: addIsAdmin ? 'rgba(245,158,11,0.08)' : 'var(--joy-palette-background-level2)',
                            border: `1px solid ${addIsAdmin ? 'rgba(245,158,11,0.3)' : 'var(--joy-palette-divider)'}`,
                            cursor: 'pointer', transition: 'all .15s',
                        }}>
                            <AdminPanelSettingsRoundedIcon sx={{ fontSize: 18, color: addIsAdmin ? '#f59e0b' : 'var(--joy-palette-text-tertiary)' }} />
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: addIsAdmin ? '#f59e0b' : 'var(--joy-palette-text-secondary)' }}>
                                Как администратору
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 0.5 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button size="sm" variant="plain" color="neutral" onClick={() => setAddOpen(false)}>Отмена</Button>
                            <Button size="sm" loading={submitting} onClick={handleAdd}
                                sx={{
                                    background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                    color: '#fff', fontWeight: 700, border: 'none',
                                    boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                                }}
                            >
                                Добавить
                            </Button>
                        </Box>
                    </Stack>
                </ModalDialog>
            </Modal>


            {/* ═══════ Модалка отмены ═══════ */}
            <Modal open={!!cancelId} onClose={() => setCancelId(null)}>
                <ModalDialog sx={{
                    maxWidth: 360, width: '100%',
                    bgcolor: 'var(--joy-palette-background-surface)',
                    border: '1px solid var(--joy-palette-divider)',
                    borderRadius: 'var(--joy-radius-xl)',
                    p: { xs: 2, sm: 2.5 },
                    maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto',
                }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mb: 1 }}>
                        Отменить штраф?
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mb: 2.5 }}>
                        Штраф будет помечен как отменённый. Он останется в истории, но не будет учитываться в расчётах.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button size="sm" variant="plain" color="neutral" onClick={() => setCancelId(null)}>Назад</Button>
                        <Button size="sm" color="warning" loading={canceling} onClick={handleCancel}>Отменить штраф</Button>
                    </Box>
                </ModalDialog>
            </Modal>


            {/* ═══════ Модалка удаления ═══════ */}
            <Modal open={!!deleteId} onClose={() => setDeleteId(null)}>
                <ModalDialog sx={{
                    maxWidth: 360, width: '100%',
                    bgcolor: 'var(--joy-palette-background-surface)',
                    border: '1px solid var(--joy-palette-divider)',
                    borderRadius: 'var(--joy-radius-xl)',
                    p: { xs: 2, sm: 2.5 },
                }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mb: 1 }}>
                        Удалить навсегда?
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mb: 2.5 }}>
                        Запись будет полностью удалена из системы. Это действие нельзя отменить.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button size="sm" variant="plain" color="neutral" onClick={() => setDeleteId(null)}>Назад</Button>
                        <Button size="sm" color="danger" loading={deleting} onClick={handleDelete}>Удалить</Button>
                    </Box>
                </ModalDialog>
            </Modal>

            {/* ═══════ Карточка последнего созданного ═══════ */}
            <AnimatePresence>
                {lastCreated && (
                    <Modal open onClose={() => setLastCreated(null)}>
                        <ModalDialog sx={{
                            maxWidth: 400, width: '100%',
                            bgcolor: 'var(--joy-palette-background-surface)',
                            border: '1px solid var(--joy-palette-divider)',
                            borderRadius: 'var(--joy-radius-xl)',
                            p: { xs: 2, sm: 2.5 },
                            maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto',
                        }}>
                            <ModalClose />
                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mb: 1.5 }}>
                                ✅ {lastCreated.is_deduction ? 'Списание добавлено' : 'Штраф добавлен'}
                            </Typography>

                            <Stack spacing={1}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)' }}>Сотрудник</Typography>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>{lastCreated.photographer_name}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)' }}>Точка</Typography>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>{lastCreated.location_name}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)' }}>Дата</Typography>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>{fmtDate(lastCreated.date)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)' }}>Сумма</Typography>
                                    <Typography className="amount-negative" sx={{ fontSize: 15, fontWeight: 800 }}>-{fmtMoney(lastCreated.value)} ₽</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', mb: 0.3 }}>Причина</Typography>
                                    <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-primary)' }}>{lastCreated.reason}</Typography>
                                </Box>
                                {lastCreated.is_administrator && (
                                    <Chip size="sm" variant="soft" startDecorator={<AdminPanelSettingsRoundedIcon sx={{ fontSize: 12 }} />}
                                        sx={{ alignSelf: 'flex-start', fontSize: 10, fontWeight: 700, bgcolor: 'rgba(245,158,11,0.10)', color: '#f59e0b' }}>
                                        Как администратору
                                    </Chip>
                                )}
                            </Stack>

                            <Divider sx={{ my: 1.5 }} />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Button size="sm" variant="plain" color="neutral" onClick={() => setLastCreated(null)}>Закрыть</Button>
                                {canDelete && (
                                    <Button size="sm" color="danger" startDecorator={<DeleteRoundedIcon sx={{ fontSize: 14 }} />}
                                        onClick={() => { setDeleteId(lastCreated.id); setLastCreated(null); }}>
                                        Удалить
                                    </Button>
                                )}
                            </Box>
                        </ModalDialog>
                    </Modal>
                )}
            </AnimatePresence>

            <UndoToast
                visible={!!undoFine}
                message={undoFine?.message || ''}
                timeout={10000}
                onUndo={handleUndoFine}
                onExpire={() => setUndoFine(null)}
            />
        </motion.div>
    );
}